import { track } from "./analytics";
import { ANALYTICS_EVENTS } from "./events";

/**
 * Lightweight Core Web Vitals capture using native PerformanceObserver APIs
 * only — no `web-vitals` dependency. Each metric fires once as a `web_vital`
 * event with { metric, value, rating }. Ratings use the same thresholds as
 * Google's web-vitals library so results are comparable to Lighthouse/CrUX.
 */

type Rating = "good" | "needs_improvement" | "poor";

function rate(metric: string, value: number): Rating {
  const thresholds: Record<string, [number, number]> = {
    LCP: [2500, 4000],
    FCP: [1800, 3000],
    CLS: [0.1, 0.25],
    INP: [200, 500],
    TTFB: [800, 1800],
  };
  const [good, poor] = thresholds[metric] ?? [0, Infinity];
  if (value <= good) return "good";
  if (value <= poor) return "needs_improvement";
  return "poor";
}

function report(metric: string, value: number) {
  track(ANALYTICS_EVENTS.WEB_VITAL, {
    metric,
    value: Math.round(metric === "CLS" ? value * 1000 : value),
    rating: rate(metric, value),
  });
}

let webVitalsInitialized = false;

export function initWebVitalsTracking() {
  if (webVitalsInitialized || typeof window === "undefined" || !("PerformanceObserver" in window)) return;
  webVitalsInitialized = true;

  try {
    // Largest Contentful Paint — keep updating until first user interaction,
    // report the final value on visibility change / pagehide.
    let lcpValue = 0;
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number; loadTime?: number };
      if (last) lcpValue = last.renderTime || last.loadTime || last.startTime;
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });

    // First Contentful Paint — fires once, report immediately.
    const paintObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          report("FCP", entry.startTime);
        }
      }
    });
    paintObserver.observe({ type: "paint", buffered: true });

    // Cumulative Layout Shift — accumulate until the page is hidden.
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (!entry.hadRecentInput) clsValue += entry.value;
      }
    });
    clsObserver.observe({ type: "layout-shift", buffered: true });

    // Interaction to Next Paint (approximation) — largest event-timing duration seen.
    let inpValue = 0;
    const eventObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (entry.duration > inpValue) inpValue = entry.duration;
      }
    });
    try {
      eventObserver.observe({ type: "event", buffered: true, durationThreshold: 40 } as any);
    } catch {
      // 'event' timing not supported in this browser — INP unavailable, non-fatal.
    }

    // Time to First Byte, from Navigation Timing — available immediately.
    const [nav] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    if (nav) report("TTFB", nav.responseStart);

    const flushFinalMetrics = () => {
      if (lcpValue > 0) report("LCP", lcpValue);
      report("CLS", clsValue);
      if (inpValue > 0) report("INP", inpValue);
    };

    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState === "hidden") flushFinalMetrics();
      },
      { once: true }
    );
    window.addEventListener("pagehide", flushFinalMetrics, { once: true });
  } catch {
    // Performance Observer entry types not supported — non-fatal, no vitals for this session.
  }
}
